import {
  Box,
  Button,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  Flex,
  HStack,
  IconButton,
  Tooltip,
  useColorMode,
  useDisclosure,
} from "@chakra-ui/react";
import { FiMenu } from "react-icons/fi";
import React from "react";
import SidebarContent from "@/src/components/docs/SideBarContent";
import { IoArrowBack } from "react-icons/io5";
import { useRouter } from "next/router";
import CustomLink from "@/src/components/CustomLink";
import useTranslation from "next-translate/useTranslation";
import { useAuth } from "@/src/contexts/AuthContext";
import { MoonIcon, SunIcon } from "@chakra-ui/icons";

export default function DocShell(props) {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const router = useRouter();
  const sidebar = useDisclosure();
  const { children, aside, ...rest } = props;
  const { colorMode, toggleColorMode } = useColorMode();

  return (
    <Flex
      as="section"
      gap={0}
      pos={"relative"}
      bg={"whiteAlpha.100"}
      maxH="100vh"
      w={"100vw"}
      {...rest}
    >
      <SidebarContent display={{ base: "none", "2xl": "unset" }}>
        {aside}
      </SidebarContent>
      <Drawer
        isOpen={sidebar.isOpen}
        onClose={sidebar.onClose}
        placement="left"
      >
        <DrawerOverlay />
        <DrawerContent>
          <SidebarContent w="full" borderRight="none">
            {aside}
          </SidebarContent>
        </DrawerContent>
      </Drawer>
      <Flex
        direction={"column"}
        gap={0}
        flex={"1 1 auto"}
        w={"full"}
        transition=".3s ease"
      >
        <Flex
          as="header"
          align="center"
          position={"fixed"}
          top={0}
          right={0}
          zIndex={2}
          justify="space-between"
          w="full"
          px="4"
          bg={`${colorMode === "light" ? "white" : "#021b17"}`}
          borderBottomWidth="1px"
          borderColor={`${colorMode === "light" ? "gray.200" : "brand.700"}`}
          h={20}
        >
          <IconButton
            aria-label="Menu"
            color={"brand.500"}
            bg={"none"}
            _hover={{
              bg: "none",
            }}
            display={{ base: "inline-flex", "2xl": "none" }}
            onClick={() => router?.back()}
            icon={<IoArrowBack size={"28px"} />}
            size="lg"
          />
          <IconButton
            aria-label="Menu"
            color={"blackAlpha.800"}
            bg={"none"}
            _hover={{
              bg: "none",
            }}
            display={{ base: "inline-flex", "2xl": "none" }}
            onClick={sidebar.onOpen}
            icon={<FiMenu size={"28px"} />}
            size="lg"
          />
          <Flex
            gap={"48px"}
            w={"full"}
            justify={"flex-end"}
            justifySelf={"flex-end"}
            alignItems={"center"}
            display={{ base: "none", "2xl": "flex" }}
          >
            <CustomLink
              fontSize={{
                base: "16px",
                sm: "16px",
                "3xl": "16px",
                "5xl": "16px",
              }}
              to={"/#features"}
            >
              {t("home:header.nav.features")}
            </CustomLink>
            <CustomLink
              fontSize={{
                base: "16px",
                sm: "16px",
                "3xl": "16px",
                "5xl": "16px",
              }}
              to={"/docs/learn-more#how-it-works"}
            >
              {t("home:header.nav.how_it_works")}
            </CustomLink>
            <CustomLink
              fontSize={{
                base: "16px",
                sm: "16px",
                "3xl": "16px",
                "5xl": "16px",
              }}
              to={"/pricing"}
            >
              {t("home:header.nav.pricing")}
            </CustomLink>
            {/*<CustomLink to={"/pocs"}>{t('home:header.nav.poc')}</CustomLink>*/}
            <CustomLink
              fontSize={{
                base: "16px",
                sm: "16px",
                "3xl": "16px",
                "5xl": "18px",
              }}
              mr={6}
              to={"/contact"}
            >
              {t("home:header.nav.contact_us")}
            </CustomLink>
            <HStack spacing={6}>
              {!isAuthenticated ? (
                <>
                  <CustomLink to={"/account/auth"}>
                    <Button
                      lineHeight={{
                        base: "24px",
                        "3xl": "36px",
                        "5xl": "38px",
                      }}
                      fontSize={{
                        base: "16px",
                        sm: "16px",
                        "3xl": "16px",
                        "5xl": "16px",
                      }}
                      letterSpacing={"0.065em"}
                      fontWeight={400}
                      variant={"outline2"}
                      colorScheme={"brand"}
                      bg={colorMode === "light" ? "transparent" : "#314542"}
                    >
                      {t("home:header.nav.login")}
                    </Button>
                  </CustomLink>
                  <CustomLink to={"/account/choose"}>
                    <Button
                      lineHeight={{
                        base: "24px",
                        "3xl": "36px",
                        "5xl": "38px",
                      }}
                      fontSize={{
                        base: "16px",
                        sm: "16px",
                        "3xl": "16px",
                        "5xl": "16px",
                      }}
                      letterSpacing={"0.065em"}
                      fontWeight={400}
                      variant={"solid"}
                      colorScheme={"brand"}
                    >
                      {t("home:header.nav.register")}
                    </Button>
                  </CustomLink>
                  <Tooltip
                    placement="bottom"
                    label={`${
                      colorMode === "light"
                        ? t("home:header.nav.dark_mode")
                        : t("home:header.nav.light_mode")
                    }`}
                  >
                    <Button
                      _hover={{ background: "#E2E8F0" }}
                      onClick={toggleColorMode}
                    >
                      {colorMode === "light" ? (
                        <MoonIcon />
                      ) : (
                        <SunIcon color="icon" />
                      )}
                    </Button>
                  </Tooltip>
                </>
              ) : (
                <>
                  <CustomLink
                    fontSize={{
                      base: "16px",
                      "2xl": "16px",
                      "3xl": "16px",
                      "5xl": "18px",
                    }}
                    ml={"85px"}
                    to={"/app/dashboard"}
                    color={"brand.600"}
                  >
                    {t("home:header.nav.goto_dashboard")}
                  </CustomLink>
                  <Tooltip
                    placement="bottom"
                    label={`${
                      colorMode === "light"
                        ? t("home:header.nav.dark_mode")
                        : t("home:header.nav.light_mode")
                    }`}
                  >
                    <Button
                      _hover={{ background: "#E2E8F0" }}
                      onClick={toggleColorMode}
                    >
                      {colorMode === "light" ? (
                        <MoonIcon />
                      ) : (
                        <SunIcon color="icon" />
                      )}
                    </Button>
                  </Tooltip>
                </>
              )}
            </HStack>
          </Flex>
        </Flex>

        <Box
          as="main"
          bg={`${colorMode === "light" ? "white" : "#021b17"}`}
          border={colorMode === "light" ? "initial" : "brand.700"}
          px="3"
          py={24}
          maxH={"100vh"}
          overflowY={"scroll"}
        >
          {children}
        </Box>
      </Flex>
    </Flex>
  );
}
